// Vendored from the host app (conek-rails
// `app/frontend/shared/lib/format-site-address-line.ts`) so the gem has no
// `shared/*` host-alias coupling. Pure function, types stripped, logic identical.
//
// Build the disambiguating address line shown under a customer name in
// company/customer selectors (CONEK-5661). Priority:
//   1. Street present -> `Street, PostalCode City, CountryCode`
//   2. No street -> `PostalCode City, CountryCode`
//   3. Otherwise -> null (the caller renders a "No address set" fallback)
//
// The postcode is kept in case 2 because it is often the only thing telling two
// same-named sites in the same town apart (CONEK-6545).
//
// Pure/i18n-free so it can be unit-tested and reused across every selector.
// Note: `state` is intentionally omitted — the ticket format does not include it.

/**
 * True when there IS an address to show but it is not the Site Address — the
 * case the selector must flag with a tooltip so it doesn't contradict the
 * Opportunity form's "No installation site address set" (CONEK-6128).
 * Undefined `isSiteAddress` (payload predating CONEK-6128, or an endpoint that
 * doesn't resolve it) is treated as "don't flag" — silence beats a wrong hint.
 */
export function showsFallbackAddress(address) {
  if (address?.isSiteAddress !== false) return false
  return formatSiteAddressLine(address) !== null
}

function clean(value) {
  // Treat null/undefined AND blank/whitespace-only as absent. `??` is wrong
  // here — an empty string must collapse to null, not survive.
  const trimmed = value?.trim()
  if (!trimmed) return null
  return trimmed
}

export function formatSiteAddressLine(address) {
  if (!address) return null

  const street = [clean(address.address), clean(address.streetNumber)]
    .filter(Boolean)
    .join(" ")
  const postalCity = [clean(address.postalCode), clean(address.city)]
    .filter(Boolean)
    .join(" ")

  // Country alone is not an address — it disambiguates nothing, and letting it
  // through would also arm the fallback-address tooltip on an empty line.
  const location = [street, postalCity].filter(Boolean).join(", ")
  if (!location) return null

  return [location, clean(address.countryCode)].filter(Boolean).join(", ")
}
